import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { APP_VERSION, BUILD_NUMBER, BUILD_TIME } from '../config/buildInfo';
import { useBrand } from '../context/BrandContext';

const Sidebar = ({ collapsed = false, onToggleSidebar }) => {
  const user = useSelector((state) => state.auth.user);
  const { brand, assets } = useBrand();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSystemAdminOpen, setIsSystemAdminOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(location.pathname.startsWith('/reports'));
  const [activeFlyout, setActiveFlyout] = useState(null);

  const toggleSystemAdminMenu = () => {
    if (collapsed) {
      setActiveFlyout(null);
      return;
    }

    setIsSystemAdminOpen(!isSystemAdminOpen);
  };

  const toggleReportsMenu = () => {
    if (collapsed) {
      setActiveFlyout(null);
      return;
    }

    setIsReportsOpen(!isReportsOpen);
  };

  const getLinkClass = (path) =>
    `sidebar-link ${location.pathname === path ? 'active' : ''}`;

  const closeMobileMenu = () => {
    setActiveFlyout(null);
    if (window.innerWidth <= 900 && !collapsed) {
      onToggleSidebar?.();
    }
  };

  const navigateFromMenu = (event, path) => {
    event.preventDefault();
    closeMobileMenu();
    navigate(path, { replace: true });
  };

  const showReportsMenu = collapsed ? activeFlyout === 'reports' : isReportsOpen;
  const showSystemAdminMenu = collapsed ? activeFlyout === 'system-admin' : isSystemAdminOpen;
  const canManageUsers = user && ['Admin', 'Quality Manager', 'Quality Staff'].includes(user.role);
  const canManageSystemMasters = user && user.role === 'Admin';

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <div className="sidebar-brand" title={brand.orgName}>
          <span className="sidebar-brand-mark">
            <img
              src={assets.markUrl}
              alt={brand.orgShortName}
            />
          </span>
          <img
            className="sidebar-brand-full"
            src={assets.logoUrl}
            alt={brand.orgName}
          />
        </div>
      </div>

      <ul className="sidebar-menu">
        <li className="sidebar-section-label">Main</li>
        <li>
          <Link
            to="/dashboard"
            className={getLinkClass('/dashboard')}
            title="Dashboard"
            onClick={(event) => navigateFromMenu(event, '/dashboard')}
          >
            <i className="bi bi-speedometer2" aria-hidden="true"></i>
            <span className="sidebar-label">Dashboard</span>
          </Link>
        </li>
        <li>
          <Link
            to="/incident"
            className={getLinkClass('/incident')}
            title="Create Incident"
            onClick={(event) => navigateFromMenu(event, '/incident')}
          >
            <i className="bi bi-plus-square" aria-hidden="true"></i>
            <span className="sidebar-label">Create Incident</span>
          </Link>
        </li>
        {canManageUsers && (
          <>
          <li className="sidebar-section-label">Reports</li>
          <li
            className={`sidebar-menu-item has-submenu ${collapsed && activeFlyout === 'reports' ? 'flyout-open' : ''}`}
            onMouseEnter={() => collapsed && setActiveFlyout('reports')}
            onMouseLeave={() => collapsed && setActiveFlyout(null)}
            onFocus={() => collapsed && setActiveFlyout('reports')}
            onBlur={(event) => collapsed && !event.currentTarget.contains(event.relatedTarget) && setActiveFlyout(null)}
          >
            <button
              className={`sidebar-link sidebar-button ${isReportsOpen ? 'open' : ''}`}
              onClick={toggleReportsMenu}
              title="Reports"
            >
              <i className="bi bi-file-earmark-spreadsheet" aria-hidden="true"></i>
              <span className="sidebar-label">Reports</span>
              <i
                className={`sidebar-chevron ${isReportsOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}`}
              ></i>
            </button>

            {showReportsMenu && (
              <ul className={`sidebar-submenu ${collapsed ? 'sidebar-flyout' : ''}`}>
                {collapsed && <li className="sidebar-flyout-title">Reports</li>}
                <li>
                  <Link
                    to="/reports/ovr-registry"
                    className={getLinkClass('/reports/ovr-registry')}
                    onClick={(event) => navigateFromMenu(event, '/reports/ovr-registry')}
                  >
                    <span className="sidebar-label">OVR Registry</span>
                  </Link>
                </li>
              </ul>
            )}
          </li>
          </>
        )}

        {canManageUsers && (
          <>
            <li className="sidebar-section-label">Administration</li>
            <li
              className={`sidebar-menu-item has-submenu ${collapsed && activeFlyout === 'system-admin' ? 'flyout-open' : ''}`}
              onMouseEnter={() => collapsed && setActiveFlyout('system-admin')}
              onMouseLeave={() => collapsed && setActiveFlyout(null)}
              onFocus={() => collapsed && setActiveFlyout('system-admin')}
              onBlur={(event) => collapsed && !event.currentTarget.contains(event.relatedTarget) && setActiveFlyout(null)}
            >
              <button
                className={`sidebar-link sidebar-button ${isSystemAdminOpen ? 'open' : ''}`}
                onClick={toggleSystemAdminMenu}
                title="System Admin"
              >
                <i className="bi bi-gear" aria-hidden="true"></i>
                <span className="sidebar-label">System Admin</span>
                <i
                  className={`sidebar-chevron ${isSystemAdminOpen ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}`}
                ></i>
              </button>

              {showSystemAdminMenu && (
                <ul className={`sidebar-submenu ${collapsed ? 'sidebar-flyout' : ''}`}>
                  {collapsed && <li className="sidebar-flyout-title">System Admin</li>}
                  <li>
                    <Link
                      to="/register"
                      className={getLinkClass('/register')}
                      onClick={(event) => navigateFromMenu(event, '/register')}
                    >
                      <span className="sidebar-label">Register User</span>
                    </Link>
                  </li>
                  {canManageSystemMasters && (
                    <>
                      <li>
                        <Link
                          to="/roles"
                          className={getLinkClass('/roles')}
                          onClick={(event) => navigateFromMenu(event, '/roles')}
                        >
                          <span className="sidebar-label">Create Role</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/departments"
                          className={getLinkClass('/departments')}
                          onClick={(event) => navigateFromMenu(event, '/departments')}
                        >
                          <span className="sidebar-label">Create Department</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/audit-logs"
                          className={getLinkClass('/audit-logs')}
                          onClick={(event) => navigateFromMenu(event, '/audit-logs')}
                        >
                          <span className="sidebar-label">Audit Logs</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/email-settings"
                          className={getLinkClass('/email-settings')}
                          onClick={(event) => navigateFromMenu(event, '/email-settings')}
                        >
                          <span className="sidebar-label">Email Settings</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/brand-settings"
                          className={getLinkClass('/brand-settings')}
                          onClick={(event) => navigateFromMenu(event, '/brand-settings')}
                        >
                          <span className="sidebar-label">Brand Settings</span>
                        </Link>
                      </li>
                    </>
                  )}
                </ul>
              )}
            </li>
          </>
        )}
      </ul>
      <div className="sidebar-version" title={BUILD_TIME ? `Build: ${BUILD_TIME}` : `Version ${APP_VERSION}`}>
        <span>v{APP_VERSION}</span>
        <small>b{BUILD_NUMBER}</small>
      </div>
    </aside>
  );
};

export default Sidebar;
